/**
 * Which usage endpoint to read, and over which window.
 *
 * The legacy `usage` handler never assigns MAU, and the field is `omitempty`, so it is absent rather than
 * zero. Only `usage_v2` counts it, and only for an Ordway subscription.
 */
export type UsageSource = { endpoint: 'usage' } | { endpoint: 'usage_v2'; start: number };

type UsageSourceInput = {
  isOrdwaySubscription: boolean;
  /** Unix seconds, or undefined when billing has not resolved one. */
  billingPeriodStart: number | undefined;
};

/**
 * Picks the usage endpoint.
 *
 * Gated on the subscription, not the account's `ordway_id`: `usage_v2` rejects an Ordway account with no
 * effective subscription, and that failure rejects the whole usage load, which non-billing pages share.
 *
 * `start` must be positive — the handler defaults an absent one to 0 and meters from the epoch.
 */
export function resolveUsageSource({ isOrdwaySubscription, billingPeriodStart }: UsageSourceInput): UsageSource {
  if (!isOrdwaySubscription) {
    return { endpoint: 'usage' };
  }

  if (billingPeriodStart == null || billingPeriodStart <= 0) {
    return { endpoint: 'usage' };
  }

  return { endpoint: 'usage_v2', start: billingPeriodStart };
}

/**
 * The cross-app cache key for a usage source. The legacy key stays query-less so embedded apps still
 * share the entry; the `usage_v2` key carries `start` so a billing-period rollover misses.
 */
export function usageCacheKey(accountId: number, source: UsageSource): string {
  if (source.endpoint === 'usage_v2') {
    return `/accounts/${accountId}/usage_v2?start=${source.start}`;
  }

  return `/accounts/${accountId}/usage`;
}
